import React, { useState, useCallback } from 'react';
import { 
  CreditCard, 
  CheckCircle, 
  AlertTriangle, 
  Calendar, 
  Shield,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiClient, { ApiError, RateLimitError } from '../utils/apiClient';

// Simple subscription status component - all authenticated users have active subscriptions
const SubscriptionStatus = ({ subscription }) => {
  return (
    <div className="flex items-center space-x-2">
      <span className="px-3 py-1 rounded-full text-sm font-medium text-green-600 bg-green-100 flex items-center space-x-1">
        <CheckCircle size={16} />
        <span>Active Subscription</span>
      </span>
      {subscription?.current_period_end && (
        <span className="text-sm text-gray-500">
          Renews {new Date(subscription.current_period_end).toLocaleDateString()}
        </span>
      )}
    </div>
  );
};

// Simplified subscription manager - just shows status and billing management
const SubscriptionManager = ({ onClose }) => {
  const { subscription } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle manage billing (redirect to Stripe Customer Portal)
  const handleManageBilling = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use the centralized API client instead of fetch
      const result = await apiClient.post('/payments/create-portal-session');
      
      if (result.portal_url) {
        // Redirect to Stripe Customer Portal
        window.location.href = result.portal_url;
      } else {
        setError('Failed to access billing portal. Please try again.');
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      
      if (err instanceof RateLimitError) {
        setError(`Too many requests. Please wait ${err.retryAfter} seconds before trying again.`);
      } else if (err instanceof ApiError) {
        setError("I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com");
      } else {
        setError('An unexpected error occurred. Please contact support@weederboard.com.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Subscription</h2>
              <p className="text-gray-600 mt-1">Manage your WeederBoard subscription</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Current Subscription Status */}
          <div className="mb-6 p-6 bg-gray-50 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Your Subscription</h3>
              <button
                onClick={handleManageBilling}
                disabled={isLoading}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium transition-colors disabled:opacity-50"
              >
                <CreditCard size={16} />
                <span>{isLoading ? 'Loading...' : 'Manage Billing'}</span>
                <ExternalLink size={14} />
              </button>
            </div>
            
            <SubscriptionStatus subscription={subscription} />
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-gray-600">
                  Next billing: {subscription?.current_period_end 
                    ? new Date(subscription.current_period_end).toLocaleDateString() 
                    : 'N/A'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-gray-400" />
                <span className="text-gray-600">All features unlocked</span>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-red-700 font-medium">Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}


          {/* Info about subscription */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-green-700 font-medium">WeederBoard Premium</p>
                <p className="text-green-600 text-sm">
                  You have full access to all WeederBoard features including unlimited deals, advanced filtering, and real-time price updates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;